import { useState, useEffect } from 'react';
import { MediaService } from '@/lib/mediaService';

interface EncryptedImageProps {
  src: string;
  alt?: string;
  className?: string;
}

export function EncryptedImage({ src, alt = '', className = '' }: EncryptedImageProps) {
  const [decryptedUrl, setDecryptedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadMedia() {
      try {
        setIsLoading(true);
        setError(null);

        const url = await MediaService.loadEncryptedMedia(src);

        if (mounted) {
          setDecryptedUrl(url);
          setError(null);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error loading encrypted image:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load image');
          setIsLoading(false);
        }
      }
    }

    loadMedia();

    return () => {
      mounted = false;
    };
  }, [src]);

  if (isLoading) {
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', minHeight: '200px' }}>
        <p>Carregando imagem...</p>
      </div>
    );
  }

  if (error || !decryptedUrl) {
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffe0e0', minHeight: '200px' }}>
        <p>Erro ao carregar imagem</p>
      </div>
    );
  }

  return <img src={decryptedUrl} alt={alt} className={className} />;
}

interface EncryptedVideoProps {
  src: string;
  className?: string;
  controls?: boolean;
}

export function EncryptedVideo({ src, className = '', controls = true }: EncryptedVideoProps) {
  const [decryptedUrl, setDecryptedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadMedia() {
      try {
        setIsLoading(true);
        setError(null);

        const url = await MediaService.loadEncryptedMedia(src);

        if (mounted) {
          setDecryptedUrl(url);
          setError(null);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error loading encrypted video:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load video');
          setIsLoading(false);
        }
      }
    }

    loadMedia();

    return () => {
      mounted = false;
    };
  }, [src]);

  if (isLoading) {
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', minHeight: '200px' }}>
        <p>Carregando vídeo...</p>
      </div>
    );
  }

  if (error || !decryptedUrl) {
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffe0e0', minHeight: '200px' }}>
        <p>Erro ao carregar vídeo</p>
      </div>
    );
  }

  return <video src={decryptedUrl} controls={controls} className={className} />;
}
